/**
 * OperatingModesCard (Phase-1 S1 / W4) — an equipment's declared operating-mode
 * model (`data.entity.operating_modes`): the modes strip (current mode
 * highlighted), then each transition (from -> to on <event>) with a compact
 * summary of the trigger the S4 predicate runtime fires on.
 *
 * READ-ONLY by default. When the caller passes `canWrite` + `onSave`, an Edit
 * button swaps this view for {@link OperatingModesEditor}, which owns the draft,
 * validates against the server's rules, and saves the whole edited entity through
 * the existing upsert. The equipment surface gates mounting on the entity
 * actually having declared modes (matching how BehaviorEquationCard gates on
 * `behavior`); the internal empty state is a safety net, not the common path.
 */

import { useState } from "react";
import { Pencil, Workflow } from "lucide-react";

import type { EntityDef, ParameterDef } from "@foundry/foundry-plant";
import type { ModeTransition, ModeTrigger } from "@/lib/plant/operating-modes";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/lib/ui/card";
import { OperatingModesEditor } from "./OperatingModesEditor";
import { OperatingModesTimeline } from "./OperatingModesTimeline";

/** A small labelled chip (mirrors BehaviorEquationCard's FieldChip). */
function Chip({ label, value }: { label: string; value: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-border bg-surface-muted px-1.5 py-0.5 text-[11px] text-on-surface-muted">
      <span className="font-medium">{label}</span>
      <span className="font-mono text-on-surface">{value}</span>
    </span>
  );
}

/** Compact chips summarising a transition's trigger (predicate + bound tag + thresholds). */
export function TriggerChips({ trigger }: { trigger?: ModeTrigger }) {
  if (!trigger?.predicate) {
    return (
      <span
        className="text-[11px] text-on-surface-muted"
        title="no predicate: a human or derived event drives this transition"
      >
        manual / derived event
      </span>
    );
  }
  const dwellMs = trigger.min_dwell_ms ?? trigger.dwell_ms;
  return (
    <div className="flex flex-wrap items-center gap-1">
      <Badge variant="secondary" className="px-1.5 py-0 font-mono text-[11px]">
        {trigger.predicate}
      </Badge>
      {trigger.tag_hint && <Chip label="tag" value={trigger.tag_hint} />}
      {/* `!= null`, not `!== undefined`: a stored trigger carries null for an unset threshold, and the page printed
          "level null" (sweep PR1, review H11). */}
      {trigger.level != null && <Chip label="level" value={String(trigger.level)} />}
      {trigger.deadband != null && <Chip label="±" value={String(trigger.deadband)} />}
      {trigger.edge && <Chip label="edge" value={trigger.edge} />}
      {trigger.value != null && <Chip label="=" value={String(trigger.value)} />}
      {dwellMs != null && <Chip label="dwell" value={`${dwellMs} ms`} />}
      {trigger.min_gap_ms != null && <Chip label="gap" value={`${trigger.min_gap_ms} ms`} />}
    </div>
  );
}

/** One transition row: from-states -> to-state, event name, trigger chips. */
function TransitionRow({ t }: { t: ModeTransition }) {
  return (
    <div className="flex flex-col gap-1 rounded-md border border-border bg-surface-muted/40 px-2.5 py-1.5">
      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        <span className="font-mono text-on-surface-muted">{(t.from ?? []).join(" | ") || "*"}</span>
        <span aria-hidden className="text-on-surface-muted">
          {"->"}
        </span>
        <span className="font-mono font-semibold text-on-surface">{t.to}</span>
        <span className="text-on-surface-muted">on</span>
        <Badge variant="outline" className="px-1.5 py-0 font-mono text-[11px]">
          {t.on_event}
        </Badge>
      </div>
      <TriggerChips trigger={t.trigger} />
    </div>
  );
}

/** Read-only modes strip: each mode chip, the current mode highlighted. */
function ModesStrip({ modes, current }: { modes: string[]; current?: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-on-surface-muted">Modes</span>
      <div className="flex flex-wrap gap-1.5">
        {modes.map((m) => {
          const isCurrent = m === current;
          return (
            <span
              key={m}
              className={
                isCurrent
                  ? "inline-flex items-center gap-1 rounded-md border border-brand bg-brand/10 px-1.5 py-0.5 text-[11px] font-medium text-on-surface"
                  : "inline-flex items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-[11px] text-on-surface"
              }
              title={isCurrent ? "declared current mode" : undefined}
            >
              <span className="font-mono">{m}</span>
              {isCurrent && <span className="text-brand">•</span>}
            </span>
          );
        })}
      </div>
    </div>
  );
}

/** Read-only strip of the parameters that declare an activation (which modes they stream in). */
function ParamActivationStrip({ params }: { params: ParameterDef[] }) {
  // Some stored entities have no `parameters` array at all (the type says it is
  // always present, but real rows can omit it) — guard so the card never crashes.
  const withModes = (params ?? []).filter((p) => (p.active_modes?.length ?? 0) > 0);
  if (withModes.length === 0) return null;
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-on-surface-muted">Parameter activation</span>
      {withModes.map((p) => (
        <div key={p.name} className="flex flex-wrap items-center gap-1.5 text-xs">
          <span className="min-w-36 font-mono text-on-surface-muted">{p.name}</span>
          {(p.active_modes ?? []).map((m) => (
            <Badge key={m} variant="outline" className="px-1.5 py-0 font-mono text-[11px]">
              {m}
            </Badge>
          ))}
        </div>
      ))}
    </div>
  );
}

export interface OperatingModesCardProps {
  entity: EntityDef;
  /** Show the Edit affordance (member+). Read-only when false/absent. */
  canWrite?: boolean;
  /** Persist an edited entity (the whole-entity upsert). Required to edit. */
  onSave?: (next: EntityDef) => void;
}

/** Operating-mode model for an entity's declared FSM: read-only, editable when allowed. */
export function OperatingModesCard({ entity, canWrite, onSave }: OperatingModesCardProps) {
  const [editing, setEditing] = useState(false);
  const om = entity.operating_modes;
  const modes = om?.modes ?? [];
  const transitions = om?.transitions ?? [];
  const editable = Boolean(canWrite && onSave);

  if (editing && onSave) {
    return (
      <OperatingModesEditor
        entity={entity}
        onSave={onSave}
        onDone={() => setEditing(false)}
      />
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-2 pb-2">
        <CardTitle className="flex flex-1 items-center gap-2 text-sm">
          <Workflow className="size-4" /> Operating modes
          {modes.length > 0 && (
            <Badge variant="secondary" className="px-1.5 py-0 text-[11px]">
              {modes.length} mode{modes.length === 1 ? "" : "s"}
            </Badge>
          )}
        </CardTitle>
        {editable && (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
            <Pencil className="size-3.5" /> Edit
          </Button>
        )}
      </CardHeader>

      <CardContent className="flex flex-col gap-3">
        {modes.length === 0 ? (
          <p className="text-sm text-on-surface-muted">
            No operating modes declared. The platform derives this equipment&apos;s
            state from the coarse global event map until modes are authored.
          </p>
        ) : (
          <ModesStrip modes={modes} current={om?.current_mode} />
        )}
        <OperatingModesTimeline entity={entity} />
        {transitions.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-on-surface-muted">Transitions</span>
            {transitions.map((t, i) => (
              <TransitionRow key={`${t.on_event}-${t.to}-${i}`} t={t} />
            ))}
          </div>
        )}
        <ParamActivationStrip params={entity.parameters} />
      </CardContent>
    </Card>
  );
}
